// KRI Matrix chart components — extracted from KRIMatrix.js to keep the
// main file leaner. Pure presentational, no state.

import React from 'react';

export function Sparkline({ values = [], width = 120, height = 28, color = '#1b2a4a' }) {
    if (!values || values.length < 2) return <span style={{ fontSize: 10, color: '#94a3b8' }}>—</span>;
    const min = Math.min(...values), max = Math.max(...values);
    const range = max - min || 1;
    const stepX = width / (values.length - 1);
    const pts = values.map((v, i) => `${i * stepX},${height - ((v - min) / range) * height}`).join(' ');
    const last = values[values.length - 1], prev = values[values.length - 2];
    const trend = last > prev ? 'up' : last < prev ? 'down' : 'flat';
    const trendColor = trend === 'up' ? '#0f7b4f' : trend === 'down' ? '#dc2626' : '#7a869a';
    return (
        <svg width={width} height={height} style={{ overflow: 'visible' }}>
            <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
            <circle cx={(values.length - 1) * stepX} cy={height - ((last - min) / range) * height} r="2.5" fill={trendColor} />
        </svg>
    );
}

export function DonutChart({ data, size = 140, thickness = 20, centerValue, centerLabel }) {
    // centerValue (0..100) and centerLabel: when provided, used in the middle
    // text instead of the auto-computed largest-slice percentage. This lets
    // callers align the donut centre with whatever "score" they're showing
    // elsewhere on the page (Score chip, parent tile) so a tool's headline
    // number is consistent everywhere.
    const total = data.reduce((s, d) => s + d.value, 0);
    if (total === 0) return null;
    const radius = size / 2;
    const inner = radius - thickness;
    let cumulative = 0;

    const getArc = (startAngle, endAngle) => {
        const start = { x: radius + inner * Math.cos(startAngle), y: radius + inner * Math.sin(startAngle) };
        const end = { x: radius + inner * Math.cos(endAngle), y: radius + inner * Math.sin(endAngle) };
        const outerStart = { x: radius + radius * Math.cos(startAngle), y: radius + radius * Math.sin(startAngle) };
        const outerEnd = { x: radius + radius * Math.cos(endAngle), y: radius + radius * Math.sin(endAngle) };
        const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
        return `M ${outerStart.x} ${outerStart.y} A ${radius} ${radius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y} L ${end.x} ${end.y} A ${inner} ${inner} 0 ${largeArc} 0 ${start.x} ${start.y} Z`;
    };

    const arcs = data.filter(d => d.value > 0).map(d => {
        const startAngle = (cumulative / total) * 2 * Math.PI - Math.PI / 2;
        cumulative += d.value;
        const endAngle = (cumulative / total) * 2 * Math.PI - Math.PI / 2;
        return { ...d, path: getArc(startAngle, endAngle) };
    });
    // Prefer caller-provided centre value/label so the donut % matches the
    // tool's official score (no two-numbers-fighting situation).
    const computedPct = total > 0 ? Math.round((data[0]?.value || 0) / total * 100) : 0;
    const showPct = (typeof centerValue === 'number' && !isNaN(centerValue)) ? centerValue : computedPct;
    const showLabel = (typeof centerLabel === 'string' && centerLabel) ? centerLabel : (data[0]?.label || '');
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {arcs.map((a, i) => (
                <path key={i} d={a.path} fill={a.color} opacity={0.9}>
                    <title>{a.label}: {a.value}</title>
                </path>
            ))}
            <text x={radius} y={radius - 6} textAnchor="middle" fontSize={size * 0.18} fontWeight={800} fill="#1b2a4a">{showPct}%</text>
            <text x={radius} y={radius + 10} textAnchor="middle" fontSize={size * 0.07} fill="#7a869a">{showLabel}</text>
        </svg>
    );
}

export function HBarChart({ data, height = 20 }) {
    return (
        <div>
            {data.map((d, i) => {
                const pct = d.maxValue > 0 ? (d.value / d.maxValue * 100) : 0;
                return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <div style={{ width: 90, fontSize: 10, color: '#3d4f6f', fontWeight: 500, textAlign: 'right', flexShrink: 0 }}>{d.label}</div>
                        <div style={{ flex: 1, height, background: '#f0f2f5', borderRadius: height / 2, overflow: 'hidden', position: 'relative' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: d.color, borderRadius: height / 2, transition: 'width 0.5s ease' }} />
                        </div>
                        <div style={{ width: 40, fontSize: 10, fontWeight: 700, color: d.color, textAlign: 'right', flexShrink: 0 }}>{d.value}%</div>
                    </div>
                );
            })}
        </div>
    );
}

export function GaugeChart({ value, threshold, criticalThreshold, size = 160 }) {
    const radius = size / 2 - 10;
    const center = size / 2;
    const startAngle = Math.PI;
    const endAngle = 2 * Math.PI;
    const valueAngle = startAngle + (value / 100) * (endAngle - startAngle);
    const threshAngle = startAngle + (threshold / 100) * (endAngle - startAngle);
    const polarToCart = (angle, r) => ({ x: center + r * Math.cos(angle), y: center + r * Math.sin(angle) });
    const arcPath = (start, end, r) => {
        const s = polarToCart(start, r);
        const e = polarToCart(end, r);
        const large = end - start > Math.PI ? 1 : 0;
        return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
    };
    const color = value >= threshold ? '#0f7b4f' : value >= criticalThreshold ? '#d97706' : '#dc2626';
    return (
        <svg width={size} height={size * 0.6} viewBox={`0 0 ${size} ${size * 0.65}`}>
            <path d={arcPath(startAngle, endAngle, radius)} fill="none" stroke="#f0f2f5" strokeWidth={12} strokeLinecap="round" />
            {value > 0 && <path d={arcPath(startAngle, valueAngle, radius)} fill="none" stroke={color} strokeWidth={12} strokeLinecap="round" />}
            {(() => { const p = polarToCart(threshAngle, radius); return <circle cx={p.x} cy={p.y} r={3} fill="#1b2a4a" opacity={0.3} />; })()}
            <text x={center} y={center - 2} textAnchor="middle" fontSize={size * 0.2} fontWeight={800} fill={color}>{value}%</text>
            <text x={center} y={center + 12} textAnchor="middle" fontSize={size * 0.06} fill="#7a869a">COMPLIANCE</text>
        </svg>
    );
}
